<template>
  <div>
    <!-- Empty State -->
    <div v-if="orcamento.itens.length === 0" class="empty-state">
      <div class="empty-state-icon">
        <fa-icon icon="box-open" size="4x" />
      </div>
      <h4 class="empty-state-title">Nenhum item no orçamento</h4>
      <p class="empty-state-description">
        Este orçamento ainda não possui itens adicionados.
      </p>
    </div>

    <!-- Tabela de Itens -->
    <div v-else>
      <CCard>
        <CCardHeader class="section-header">
          <fa-icon icon="box" class="mr-2" />
          <strong>Itens do Pedido</strong>
          <CBadge color="light" class="ml-2">{{ orcamento.itens.length }} item(ns)</CBadge>
        </CCardHeader>
        <CCardBody class="p-0">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th width="80" class="text-center">Imagem</th>
                  <th width="80">Cód.</th>
                  <th width="60" class="text-center">Seq.</th>
                  <th>Produto</th>
                  <th width="80" class="text-center">Qtd.</th>
                  <th width="120" class="text-right">Vl. Unit.</th>
                  <th width="140" class="text-right">Vl. Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in orcamento.itens" :key="item.produto_id" :class="{'row-modificado': item.motivo}">
                  <td class="text-center">
                    <div class="product-thumbnail">
                      <img :src="cdnUrl(item.produto.imagem_id || item.produto_id)" :alt="item.produto.descricaonotafiscal"/>
                    </div>
                  </td>
                  <td>
                    <CBadge color="secondary">{{ item.produto_id }}</CBadge>
                  </td>
                  <td class="text-center">
                    <CBadge color="info">{{ item.sequenciainsercao }}</CBadge>
                  </td>
                  <td>
                    <div class="product-name">
                      <GetValue :objeto="item.produto" chave="descricaonotafiscal" />
                    </div>
                    <div v-if="item.motivo" class="product-note">
                      <fa-icon icon="info-circle" class="mr-1" />
                      {{ item.motivo }}
                    </div>
                  </td>
                  <td class="text-center">
                    <strong>{{ item.quantidade }}</strong>
                  </td>
                  <td class="text-right">{{ formatMoney(item.valorunitario) }}</td>
                  <td class="text-right">
                    <strong class="text-success">{{ formatMoney(item.valortotal) }}</strong>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="table-total">
                  <td colspan="6" class="text-right">
                    <strong>TOTAL GERAL:</strong>
                  </td>
                  <td class="text-right">
                    <strong class="text-success total-value">{{ formatMoney(totalGeral) }}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CCardBody>
      </CCard>
    </div>
  </div>
</template>

<script>

import ChaveValor from "@/components/base/ChaveValor.vue";
import GetValue from "@/components/base/GetValue.vue";
import {cdnUrlProduto} from "@/helpers/url";

export default {
  name: "itens-orcamento",
  props: ["orcamento"],
  components: {GetValue, ChaveValor },
  computed: {
    totalGeral() {
      if (!this.orcamento.itens || this.orcamento.itens.length === 0) {
        return 0;
      }
      return this.orcamento.itens.reduce((sum, item) => {
        return sum + parseFloat(item.valortotal || 0);
      }, 0);
    }
  },
  methods: {
    cdnUrl(id) {
      return cdnUrlProduto(id);
    },
    formatMoney(value) {
      if (!value) return 'R$ 0,00';
      const num = parseFloat(value);
      return 'R$ ' + num.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
  }
};
</script>

<style scoped>
/* Section Headers */
.section-header {
  background: #f8f9fa;
  color: #2d3748;
  font-weight: 600;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  align-items: center;
}

.section-header .svg-inline--fa {
  color: #6c757d;
  margin-right: 0.5rem;
}

/* Cards */
.card {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: #f8f9fa;
  border-radius: 0.25rem;
  margin-top: 1rem;
  border: 2px dashed #ced4da;
}

.empty-state-icon {
  color: #ced4da;
  margin-bottom: 1.5rem;
}

.empty-state-title {
  color: #2d3748;
  font-weight: 600;
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
}

.empty-state-description {
  color: #6c757d;
  font-size: 1rem;
  max-width: 500px;
  margin: 0 auto;
}

/* Product Thumbnail */
.product-thumbnail {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 0.25rem;
  border: 1px solid #dee2e6;
  background: #f8f9fa;
  margin: 0 auto;
}

.product-thumbnail img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* Product Info */
.product-name {
  font-weight: 500;
  color: #212529;
  line-height: 1.4;
}

.product-note {
  font-size: 0.8125rem;
  color: #dc3545;
  margin-top: 0.25rem;
  font-style: italic;
}

/* Table Styling */
.table thead th {
  background-color: #f8f9fa;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #dee2e6;
  padding: 0.75rem;
  color: #6c757d;
}

.table tbody td {
  vertical-align: middle;
  padding: 0.75rem;
}

.table tbody tr:hover {
  background-color: #f8f9fa;
}

.row-modificado {
  background-color: #fff3cd;
  border-left: 3px solid #ffc107;
}

.row-modificado:hover {
  background-color: #ffe69c;
}

/* Table Total */
.table-total {
  background-color: #f8f9fa;
  border-top: 2px solid #dee2e6;
  font-size: 1.0625rem;
}

.table-total td {
  padding: 1rem 0.75rem;
}

.total-value {
  font-size: 1.25rem;
  color: #28a745;
}

/* Utility */
.mr-1 {
  margin-right: 0.25rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.ml-2 {
  margin-left: 0.5rem;
}
</style>
